<template>
  <v-container>
    <v-layout text-xs wrap>
      <v-flex mb-4>
        <v-stepper v-model="step" class="step" alt-labels>
          <v-stepper-header class="step_header">
            <v-stepper-step :complete="step > 1" step="1">Contact Info</v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 2" step="2">Child's Birthday</v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 3" step="3">Home Address</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 4" step="4">Special Circumstances</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="5">Family Income</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Contact Information -->
            <v-stepper-content step="1">
              <v-form v-model="step1_valid" ref="form1">
                <p
                  class="subheading font-weight-regular"
                >Please enter your contact information. We'll only use this information to contact you regarding enrolling in publicly funded preschool programs. <a @click="$router.push({name: 'privacy'})">View privacy policy</a></p>
                <v-text-field
                  v-model="parentName"
                  :rules="[rules.required]"
                  label="Parent or Guardian Name"
                  outline
                ></v-text-field>
                <v-text-field v-model="email" :rules="[rules.required, rules.email]" label="E-mail" outline></v-text-field>
                <v-text-field v-model="phone" mask="phone" label="Cell Phone" outline></v-text-field>
                <p class="subheading font-weight-regular">How did you hear about Palmetto Pre-K?</p>
                <v-radio-group v-model="howDidHear" :rules="[rules.required]">
                  <v-radio label="AmeriCorps" value="americorps"></v-radio>
                  <v-radio label="Childcare Center" value="childcare"></v-radio>
                  <v-radio label="Facebook" value="facebook"></v-radio>
                  <v-radio label="Friend/Neighbor/Colleague" value="friend"></v-radio>
                  <v-radio label="Google Search" value="googlesearch"></v-radio>
                  <v-radio label="Postcard" value="postcard"></v-radio>
                  <v-radio label="Radio" value="radio"></v-radio>
                  <v-radio label="Text Message" value="text"></v-radio>
                  <v-layout class="other-radio">
                    <v-radio label="Other" value="other" class=""></v-radio>
                    <v-text-field class="other-input" v-model="other" single-line outline></v-text-field>
                  </v-layout>
                </v-radio-group>

                <v-checkbox id="consent" name="consent" value="accept" :rules="[rules.required]" class="consent">
                  <template v-slot:label>
                    <p>
                      Yes, I consent to the collection and/or usage by SC First Steps or our partners of your personal information as referred to or otherwise stated in the <a target="_blank" href="https://first5sc.org/privacy-policy">Privacy Policy and Terms of Use.</a>
                    </p>
                  </template>
                </v-checkbox>
                  
                <v-btn round color="primary" @click="contactInfoSubmit"><span class="btn-white">Next Step</span></v-btn>
              </v-form>
            </v-stepper-content>

            <!-- Birthday Information -->
             <v-stepper-content step="2">
               <p
                class="subheading font-weight-regular">
                Please enter your child's birthday.</p>
              <v-form ref="form4">
              <v-layout row wrap>
                <v-flex xs12 sm5>
                  <v-select v-model="birthdayMonth" :items="birthday_months" :rules="[rules.required]" label="Month" outline></v-select>
                </v-flex>
                <v-flex xs12 sm2>
                  <v-select v-model="birthdayDay" :items="birthday_days" :rules="[rules.required]" label="Day" outline></v-select>
                </v-flex>
                <v-flex xs12 sm5>
                  <v-select v-model="birthdayYear" :items="birthday_years" :rules="[rules.required]" label="Year" outline></v-select>
                </v-flex>
              </v-layout>
              </v-form>
              <p v-if="invalidBirthDate" class="error">
                You have entered an invalid birth date. Please enter the correct date.
              </p>
              <br />
              <v-btn round class="outlined" @click="step = 1" flat><span class="btn-black">Back</span></v-btn>
              <v-btn round color="primary" @click="birthdaySubmit"><span class="btn-white">Next Step</span></v-btn>
            </v-stepper-content>

            <!-- Address Information -->
            <v-stepper-content step="3">
              <p
                class="subheading font-weight-regular">
                Please enter your home address. Eligibility in some programs is dependent on your home address.
                <br />
                Please select address from the dropdown below to proceed.
                </p>

              <vuetify-google-autocomplete
                id="map"
                append-icon="search"
                v-on:placechanged="getAddressData"
                placeholder="Start typing your address"
                :rules="[rules.required]"
              ></vuetify-google-autocomplete>
              <v-form ref="form_apt">
                <v-layout row wrap>
                  <v-flex>
                    <v-text-field label="Apartment Number" v-model="aptNumber"></v-text-field>
                  </v-flex>
                </v-layout>
              </v-form>
              <div>
                {{schoolDistrictStatus}}
                <div id="districtMap"></div>
              </div>
              <v-btn round class="outlined" @click="step = 2" flat><span class="btn-black">Back</span></v-btn>
              <v-btn round color="primary" @click="addressSubmit"><span class="btn-white">Next Step</span></v-btn>
            </v-stepper-content>

            <!-- Special Circumstances -->
            <v-stepper-content step="4">
                <p>Please select all that may apply</p>
                <v-form ref="formSpecial" style="padding-bottom: 20px;">
                    <div v-if="special_circumstances.filter(x => x.Category == 'Household Circumstances').length > 0">
                      <strong>Household Circumstances:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Household Circumstances')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'Developmental Delays and Special Needs').length > 0" style="padding-top:20px">
                      <strong>Developmental Delays and Special Needs:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Developmental Delays and Special Needs')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'Government Benefits').length > 0" style="padding-top:20px">
                      <strong>Are you already eligible or receiving government benefits?</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Government Benefits')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'High Risk Pregnancy').length > 0" style="padding-top:20px">
                      <strong>High Risk Pregnancy:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'High Risk Pregnancy')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'Adoption / Foster Care').length > 0" style="padding-top:20px">
                      <strong>Adoption / Foster Care:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Adoption / Foster Care')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'Abuse / Neglect').length > 0" style="padding-top:20px">
                      <strong>Abuse / Neglect:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Abuse / Neglect')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                    <div v-if="special_circumstances.filter(x => x.Category == 'Additional Optional Household Circumstances').length > 0" style="padding-top:20px">
                      <strong>Additional Optional Household Circumstances:</strong>
                      <ul>
                          <li v-for="(e,index) in special_circumstances.filter(x => x.Category == 'Additional Optional Household Circumstances')" v-bind:key="e.value">
                            <v-checkbox
                                :key="index"
                                v-model="specialChecked"
                                :value="e.value"
                                :hide-details="index !== (special_circumstances.length -1)"
                            >
                              <template v-slot:label>
                                <div>
                                  {{e.text}}
                                  <v-icon v-if="e.ShowHelpText" @click="openInfo(e.value)" @click.stop>info</v-icon>
                                </div>
                              </template>
                            </v-checkbox>
                          </li>
                      </ul>
                    </div>
                </v-form>
                <v-btn round class="outlined" @click="step = 3" flat><span class="btn-black">Back</span></v-btn>
                <v-btn round color="primary" @click="specialSubmit"><span class="btn-white">Next Step</span></v-btn>

            </v-stepper-content>

            <!-- Income Requirements -->
            <v-stepper-content step="5">
              <p>
                Please enter your family's size including yourself, and your family's annual gross income (before taxes are taken out).
              </p>
              <v-form ref="form3">
                <v-layout row wrap>
                  <v-flex xs12 sm6>
                    <v-select v-model="peopleInHousehold" :items="people_in_house_options" :rules="[rules.required]" label="Persons in Household" outline></v-select>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <div class="v-input v-text-field v-text-field--enclosed v-text-field--outline theme--light">
                      <div class="v-input__control">
                        <div class="v-input__slot">
                          <div class="v-text-field__slot">
                            <label aria-hidden="true" class="v-label v-label--active theme--light" style="left: 0px; right: auto; position: absolute;">
                              Annual Income
                            </label>
                            <CurrencyInput aria-label="Annual Income" class="" v-model="householdIncome" :options="currencyOptions" required></CurrencyInput>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-form>

              <v-btn round class="outlined" @click="step = 4" flat><span class="btn-black">Back</span></v-btn>
              <v-btn round color="primary" @click="incomeSubmit"><span class="btn-white">Next Step</span></v-btn>
            </v-stepper-content>

           
          </v-stepper-items>
        </v-stepper>
      </v-flex>

      <v-dialog v-model="popup_invalid_school_district" max-width="750">
        <v-card fluid>
          <v-card-title class="headline">Sorry you are not eligible for Pre-K</v-card-title>

          <v-card-text>Based on the information you entered, you do not live in a valid South Carolina school district. </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-layout row wrap>
              <v-btn round style="margin-bottom: 20px; margin-left: 10px;"
                color="primary"
                @click="popup_invalid_school_district = false"
              >Edit Home Address</v-btn>

              <v-btn round style="margin-bottom: 20px;"
                color="primary"
                href="https://ed.sc.gov/districts-schools/schools/school-directory/"
                target="_blank"
              >Contact Local School District</v-btn>
              <v-btn round style="margin-bottom: 20px;"
                color="primary"
                href="http://sc-headstart.com/"
                target="_blank"
              >Contact Head Start</v-btn>
            </v-layout>
            
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showHelpText" width="auto ">
        <v-card fluid>
          <v-card-text>
            <div v-html="helpText"></div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import CurrencyInput from "./CurrencyInput.vue"
export default {
  components: {CurrencyInput},
  data: function() {
    return {
      showHelpText: false,
      helpText: "",
      leadId: null,
      eligible: false,
      summerEligible: false,
      anyEligible: false,
      address: "",
      aptNumber: "",
      address_school_district: "",
      step: 0,
      howDidHear: "",
      other: "",
      parentName: "",
      email: "",
      phone: "",
      schoolDistrictStatus: "",
      peopleInHousehold: 0,
      householdIncome: 0.0,
      medicaidElligable: false,
      step1_valid: false,
      step2_vaild: false,
      step3_valid: false,
      step4_valid: false,
      popup_invalid_school_district: false,
      pilot_program: false,
      popup_income: false,
      popup_too_young: false,
      popup_too_old: false,
      programs:[],
      specialChecked: [],
      special_circumstances: [],
      currencyOptions: {
        locale: undefined,
        currency: 'USD',
        currencyDisplay: 'symbol',
        valueRange: undefined,
        precision: 2,
        hideCurrencySymbolOnFocus: false,
        hideGroupingSeparatorOnFocus: false,
        hideNegligibleDecimalDigitsOnFocus: false,
        autoDecimalDigits: true,
        exportValueAsInteger: false,
        autoSign: true,
        useGrouping: true
      },
      rules: {
        required: value => !!value || "Required.",
        counter: value => value.length <= 20 || "Max 20 characters",
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        }
      },
      people_in_house_options: ["2", "3", "4", "5", "6", "7", "8"],
      birthday: new Date(),
      birthdayMonth: null,
      birthdayYear: null,
      birthdayDay: null,
      invalidBirthDate: false,
      birthday_months: [
        {text: "January", value: '01'},
        {text: "February", value: '02'},
        {text: "March", value: '03'},
        {text: "April", value: '04'},
        {text: "May", value: '05'},
        {text: "June", value: '06'},
        {text: "July", value: '07'},
        {text: "August", value: '08'},
        {text: "September", value: '09'},
        {text: "October", value: '10'},
        {text: "November", value: '11'},
        {text: "December", value: '12'}
      ],
      birthday_days: [
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
        "19",
        "20",
        "21",
        "22",
        "23",
        "24",
        "25",
        "26",
        "27",
        "28",
        "29",
        "30",
        "31"
      ],
      birthday_years: [
        "2025",
        "2024",
        "2023",
        "2022",
        "2021",
        "2020",
        "2019",
        "2018",
        "2017",
        "2016",
        "2015",
        "2014",
        "2013",
        "2012",
        "2011"
      ],
      districts: {
          "abbeville":"Abbeville" ,
          "aiken":"Aiken" ,
          "allendale":"Allendale" ,
          "anderson-1":"Anderson 1" ,
          "anderson-2":"Anderson 2" ,
          "anderson-3":"Anderson 3" ,
          "anderson-4": "Anderson 4" ,
          "anderson-5": "Anderson 5" ,
          "bamberg-1":"Bamberg 1" ,
          "bamberg-2" : "Bamberg 2" ,
          "barnwell-19":"Barnwell 19" ,
          "barnwell-29":"Barnwell 29",
          "barnwell-45":"Barnwell 45",
          "beaufort":"Beaufort",
          "berkeley":"Berkeley",
          "calhoun":"Calhoun",
          "charleston":"Charleston",
          "cherokee":"Cherokee",
          "chester":"Chester",
          "chesterfield":"Chesterfield",
          "clarendon-1":"Clarendon 1",
          "clarendon-2":"Clarendon 2",
          "clarendon-3":"Clarendon 3",
          "colleton":"Colleton",
          "darlington":"Darlington",
          "dillon-3":"Dillon 3",
          "dillon-4":"Dillon 4",
          "dorchester-2":"Dorchester 2",
          "dorchester-4":"Dorchester 4",
          "edgefield":"Edgefield",
          "fairfield":"Fairfield",
          "florence-1":"Florence 1",
          "florence-2":"Florence 2",
          "florence-3":"Florence 3",
          "florence-4":"Florence 4",
          "florence-5":"Florence 5",
          "georgetown":"Georgetown",
          "greenville":"Greenville",
          "greenwood-50":"Greenwood 50",
          "greenwood-51":"Greenwood 51",
          "greenwood-52":"Greenwood 52",
          "hampton-1":"Hampton 1",
          "hampton-2":"Hampton 2",
          "horry":"Horry",
          "jasper":"Jasper",
          "kershaw":"Kershaw",
          "lancaster":"Lancaster",
          "laurens-55":"Laurens 55",
          "laurens-56":"Laurens 56",
          "lee":"Lee",
          "lexington-1":"Lexington 1",
          "lexington-2":"Lexington 2",
          "lexington-3":"Lexington 3",
          "lexington-4":"Lexington 4",
          "lexingtonrichland-5":"Lex-Rich 5",
          "marion":"Marion",
          "marlboro":"Marlboro",
          "mccormick":"McCormick",
          "newberry":"Newberry",
          "oconee":"Oconee",
          "orangeburg":"Orangeburg",
          "pickens":"Pickens",
          "richland-1":"Richland 1",
          "richland-2":"Richland 2",
          "saluda":"Saluda",
          "spartanburg-1":"Spartanburg 1",
          "spartanburg-2":"Spartanburg 2",
          "spartanburg-3":"Spartanburg 3",
          "spartanburg-4":"Spartanburg 4",
          "spartanburg-5":"Spartanburg 5",
          "spartanburg-6":"Spartanburg 6",
          "spartanburg-7":"Spartanburg 7",
          "sumter":"Sumter",
          "union":"Union",
          "williamsburg": "Williamsburg",
          "york-1":"York 1",
          "york-2":"York 2",
          "york-3":"York 3",
          "york-4":"York 4"
        }
    };
  },
  mounted() {
 
    this.$gtag.event('conversion',  {'send_to': 'AW-670219579/xoUpCJeRksMBELv6yr8C'});

    fetch(this.$apiBaseUrl + "/umbraco/api/programs/getallprograms")
      .then((response) => response.json())
      .then((data) => (this.programs = data));

    fetch(this.$apiBaseUrl + "/umbraco/api/programs/getallcriteria")
      .then((response) => response.json())
      .then((data) => (this.special_circumstances = data));
  },
  methods: {
    contactInfoSubmit: function() {
      if(this.$refs.form1.validate()){
        var lead;
        if(this.leadId){
          lead = {
            "LeadId": this.leadId,
            "ParentName": this.parentName,
            "Email": this.email,
            "Phone": this.phone,
            "ReferralType": this.howDidHear,
            "ReferralNote": this.other,
            "Source": "Palmetto Pre-K"
          };
        }
        else{
          lead = {
            "ParentName": this.parentName,
            "Email": this.email,
            "Phone": this.phone,
            "ReferralType": this.howDidHear,
            "ReferralNote": this.other,
            "Source": "Palmetto Pre-K"
          }
        }
        this.postLead(lead);
        this.step = 2;
        window.scrollTo(0,0);
      }
    },
    birthdaySubmit: function() {
      this.invalidBirthDate = false;
      if (!this.$refs.form4.validate()) {
        return false;
      }
      var birthdayDayNew = this.birthdayDay;
      if(this.birthdayDay.length < 2){
        birthdayDayNew = '0' + this.birthdayDay;
      }
      var birthdayString = this.birthdayYear + '-' + this.birthdayMonth + '-' + birthdayDayNew + 'T00:00:00.000Z';
      var birthday = Date.parse(birthdayString);
      
      //check if valid date
      if (isNaN(birthday)) {
        this.invalidBirthDate = true;
        return false;
      }

      birthday = new Date(birthdayString);
      
      if (birthday.getUTCMonth() != (this.birthdayMonth - 1) )
      {
        this.invalidBirthDate = true;
        return false;
      }

      this.birthday = birthday;
      this.checkEligibilityBirthday();
      this.step = 3;
      window.scrollTo(0,0);

    },

    /**
     * When the location found
     * @param {Object} addressData Data of the found location
     * @param {Object} placeResultData PlaceResult object
     * @param {String} id Input container ID
     */
    getAddressData: function(addressData, placeResultData, id) {
      this.address = addressData;
      console.log("address selected", addressData, placeResultData, id);
    },
    addressSubmit: function() {
      if (this.address) {
        this.checkSchoolDistrict();
      }
      else {
        return false;
      }
    },
    specialSubmit: function() {
        this.step = 5;
        window.scrollTo(0,0);
    },
    incomeSubmit: function() {
      this.checkEligibilityIncome();
      this.checkEligibilitySpecial();
      this.goToResults();
    },
    checkEligibilityBirthday: function(){
      var that = this;

        var today = new Date();
        var isPrenatal = false;
        if(that.birthday > today){
          isPrenatal = true;
        }

        for(var i = 0; i < that.programs.length; i++){
            that.programs[i].IneligibleAge = false;
            var dateToCheck = new Date(that.programs[i].Age.AgeOfDate);
            var minAge = that.programs[i].Age.MinimumAge;
            var maxAge = that.programs[i].Age.MaximumAge;
            var age = this.ageOnDate(that.birthday, dateToCheck);
            if(!isPrenatal){
              if(minAge <= age && age <= maxAge){
                  that.programs[i].Ineligible = false;
              }
              else{
                  that.programs[i].IneligibleAge = true;
              }
            }
            else{
              if(that.programs[i].Age.IncludesPrenatal){
                that.programs[i].Ineligible = false;
              }
              else{
                that.programs[i].IneligibleAge = true;
              }
            }
            
        }
    },
    checkSchoolDistrict: function() {
      if (this.address != null) {
       // this.schoolDistrictStatus = "Determining School District...";
        var that = this;
        var map = new window.google.maps.Map(
          document.getElementById("districtMap"),
          {
            zoom: 6,
            center: { lat: 34, lng: -81 }
          }
        );
        map.data.loadGeoJson("/sc_school_districts.json", {}, function(
          features
        ) {
          var found = false;
          for (var i = 0; i < features.length; i++) {
            var feature = features[i];
            var gaGeom = feature.getGeometry();
            var poly = new window.google.maps.Polygon({
              paths: gaGeom.getAt(0).getArray(),
              map: map,
              clickable: false
            });
            var point = new window.google.maps.LatLng(
              that.address.latitude,
              that.address.longitude
            );
            var inside = window.google.maps.geometry.poly.containsLocation(
              point,
              poly
            );
            console.log(
              "checking district",
              feature.getProperty("slug"),
              inside
            );
            if (inside) {
              that.address_school_district = feature.getProperty("slug");
              console.log("In school district " + that.address_school_district);
              found = true;
              break;
            }
          }

          if (found) {
            that.checkEligibilityLocation();

            if (that.address) {
              that.addressString = that.address.street_number + ' ' + that.address.route + ', ' + that.address.locality + ', ' + that.address.administrative_area_level_1 + ' ' + that.address.postal_code;
            }

            var lead;
            if(that.leadId){
              lead = {
                "ParentName": that.parentName,
                "Email": that.email,
                "Phone": that.phone,
                "ReferralType": that.howDidHear,
                "ReferralNote": that.other,
                "Source": "Palmetto Pre-K",
                "LeadId": that.leadId,
                "Address": that.addressString,
                "ApartmentNumber": that.aptNumber,
                "County": that.address.administrative_area_level_2,
                "SchoolDistrict": that.districts[that.address_school_district]
              };
            }
            else{
              lead = {
                "ParentName": that.parentName,
                "Email": that.email,
                "Phone": that.phone,
                "ReferralType": that.howDidHear,
                "ReferralNote": that.other,
                "Source": "Palmetto Pre-K",
                "Address": that.addressString,
                "ApartmentNumber": that.aptNumber,
                "County": that.address.administrative_area_level_2,
                "SchoolDistrict": that.districts[that.address_school_district]
              };
            }

            that.postLead(lead);

            that.step = 4;
            window.scrollTo(0,0);
          }else{
           
            this.$gtag.event('conversion',  {'send_to': 'AW-670219579/uDX9CJSvjcMBELv6yr8C'});
            //address not in a SC school district
            that.popup_invalid_school_district = true;
          }
        });
      }
    },
    checkEligibilityLocation: function() {
      var that = this;
      if(that.address){
        var county = that.address.administrative_area_level_2;
        var postal = that.address.postal_code;
        var schoolDistrict = null;
        if(that.address_school_district){
          schoolDistrict = that.districts[that.address_school_district];
        }
        for(var i = 0; i < that.programs.length; i++){
          that.programs[i].CountyIneligible = false;
          that.programs[i].ZipIneligible = false;
          that.programs[i].SchoolDistrictIneligible = false;
          if(county){
            if(that.programs[i].Counties && that.programs[i].Counties.length > 0){
              var matchingCounties = that.programs[i].Counties.filter(x => x == county);
              if(matchingCounties.length == 0){
                that.programs[i].CountyIneligible = true;
              }
            }
          }
          if(schoolDistrict){
            if(that.programs[i].Districts && that.programs[i].Districts.length > 0){
              var matchingDistricts = that.programs[i].Districts.filter(x => x == schoolDistrict);
              if(matchingDistricts.length == 0){
                that.programs[i].SchoolDistrictIneligible = true;
              }
            }
          }
          if(postal){
            if(that.programs[i].ZipCodes && that.programs[i].ZipCodes.length > 0){
              var matchingZips = that.programs[i].ZipCodes.filter(x => x == postal);
              if(matchingZips.length == 0){
                that.programs[i].ZipIneligible = true;
              }
            }
          }

        }
        
      }
    },
    checkEligibilityIncome: function(){
        var that = this;

        for(var i = 0; i < that.programs.length; i++){
            that.programs[i].IneligibleIncome = false;
            
            var income = that.programs[i].Incomes.filter(function(j){
                return j.PeopleInHousehold == that.peopleInHousehold;
            });
            if(income.length > 0){
              if(that.householdIncome > income[0].MaximumIncome){
                  that.programs[i].IneligibleIncome = true;              
              }
            }
            
        }
    },
    checkEligibilitySpecial: function(){
        var that = this;
        for(var i = 0; i < that.programs.length; i++){
            that.programs[i].IneligibleSpecial = false;
            that.programs[i].OverrideIncome = false;
            
            var totalReq = 0;

            if(that.programs[i].IncomeAsRequired){      
              if(that.programs[i].IneligibleIncome){
                that.programs[i].IneligibleIncome = false;
              }
              else{
                totalReq++;
              }
            }

            for(var j = 0; j < that.programs[i].Criteria.length; j++){
                var criteriaId = that.programs[i].Criteria[j].CriteriaId;
                var criteriaType = that.programs[i].Criteria[j].Type;
                if(that.specialChecked.includes(criteriaId)){
                    if(criteriaType == "Override Income"){
                        that.programs[i].OverrideIncome = true;
                    }
                    else if(criteriaType == "Required"){
                      totalReq++;
                    }
                }
                else {
                    if(criteriaType == "Required" && that.programs[i].MinimumRequired == 0){
                        if(that.programs[i].ProgramId == 1088){
                          console.log('missing id: ' + criteriaId);
                        }
                        that.programs[i].IneligibleSpecial = true;
                    }

                    if(criteriaType == "Absolute"){
                      that.programs[i].IneligibleSpecial = true;
                    }
                }
            }

            if(that.programs[i].MinimumRequired > 0 && that.programs[i].MinimumRequired > totalReq){
              that.programs[i].IneligibleSpecial = true;
            }
        }
    },
    goToResults: function() {
      var that = this;
      var eligibleList = '';

      for(var i = 0; i < that.programs.length; i++){
          if(that.programs[i].CountyIneligible || that.programs[i].SchoolDistrictIneligible || that.programs[i].ZipIneligible || that.programs[i].IneligibleAge || 
                        that.programs[i].IneligibleSpecial || (that.programs[i].IneligibleIncome && !that.programs[i].OverrideIncome)){
              that.programs[i].Ineligible = true;
          }
          else{
            if(that.programs[i].ShowOnPPK){
              that.anyEligible = true;
            }
            
            eligibleList += that.programs[i].ProgramId + '|';
          }
      }

      //this is where we will save the lead to the db and set local storage item for use on results page
      eligibleList = eligibleList.slice(0, -1);
      var lead;
      if(that.leadId){
        lead = {
          "ParentName": that.parentName,
          "Email": that.email,
          "Phone": that.phone,
          "ReferralType": that.howDidHear,
          "ReferralNote": that.other,
          "Source": "Palmetto Pre-K",
          "LeadId": that.leadId,
          "Address": that.addressString,
          "ApartmentNumber": that.aptNumber,
          "County": that.address.administrative_area_level_2,
          "SchoolDistrict": that.districts[that.address_school_district],
          "EligiblePrograms": eligibleList
        };
      }
      else{
        lead = {
          "ParentName": that.parentName,
          "Email": that.email,
          "Phone": that.phone,
          "ReferralType": that.howDidHear,
          "ReferralNote": that.other,
          "Source": "Palmetto Pre-K",
          "Address": that.addressString,
          "ApartmentNumber": that.aptNumber,
          "County": that.address.administrative_area_level_2,
          "SchoolDistrict": that.districts[that.address_school_district],
          "EligiblePrograms": eligibleList
        };
      }

      that.postLead(lead);
      localStorage.setItem("programs", JSON.stringify(that.programs));
      localStorage.setItem("anyEligible", that.anyEligible);
      localStorage.setItem("leadId", that.leadId);
      this.$router.push({ path: 'eligible'});

    },
    ageOnDate: function(birthday, dateToCheck){
        var checkDate = new Date(dateToCheck);
        var birthDate = new Date(birthday);
        var age = checkDate.getFullYear() - birthDate.getFullYear();
        var m = checkDate.getMonth() - birthDate.getMonth();
        age = age * 12 + m;
        if(checkDate.getMonth() == birthDate.getMonth()){
          if(checkDate.getDate() <= birthDate.getDate()){
            age--;
          }
        }
        return age;
    },
    openInfo: function(value){
      var criteria = this.special_circumstances.find(x => x.value == value);
      if(criteria){
        this.helpText = criteria.HelpText;
        this.showHelpText = true;
      }  
    },
    postLead: function(lead){
      fetch(this.$apiBaseUrl + '/umbraco/api/leadapi/postlead',
      {
        method: 'POST',
        headers: 
          {
            'Content-Type': 'application/json'
          },
        body: JSON.stringify(lead)
      })
      .then(response => response.json())
      .then(data =>{
        this.leadId = data;
      });
    }
  }
};
</script>

<style scoped>
  ul li{
    padding: 0;
    list-style: none;
  }

  .step{
    border: none;
    box-shadow: none;
  }

  .step_header{
    box-shadow: none;
  }

  .other-radio {
    align-items:start;
  }

  .consent {
    margin-top: 0;
  }

  .v-input--radio-group .v-input__slot{
    margin-bottom: 0;
  }

  .form-group {
    border: 2px solid rgba(0, 0, 0, 0.54);
  }

  .form-group input {
    text-align: end;
    padding-right: 20px;
  }

</style>
